import React, { useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { BackBoldArrowIcon, CalendarIcon } from '@gr-monorepo/common/public/assets/icons';
import { Loader } from '@gr-monorepo/common/public/ui';
import { PrimaryButton } from '@gr-monorepo/common/public/ui/Buttons';
import { supportEmail } from '@gr-monorepo/common/public/ui/constants';
import { LogClick } from 'features/shared/ClickStream';
import { useAuthContext } from 'hooks/useAuthContext';
import useUserData from 'hooks/useUserData';
import { OlympiadStatus as DetailsOlympiadStatus } from 'features/olympiads/model';
import OlympiadCard from '../OlympiadCard/OlympiadCard';
import OlympiadsManagerCard from '../OlympiadsManagerCard/OlympiadsManagerCard';
import { isTableStatisticYear, OlympiadEducationYear, readSelectedOlympiadYear } from '../../constants/years';
import { defaultOlympiadStatusesFilter, Olympiad, OlympiadStatus } from '../../models/Olympiad';
import { useOlympiadsFormControl } from '../../OlympiadsFormControl';
import { OlympiadsContent, OlympiadsPage, OlympiadsSidebar } from '../../OlympiadsListStyled';
import { useOlympiadStatisticDetails } from './useOlympiadStatisticDetails';
import { useOlympiadStatisticDownload, BtnState, type LevelId } from './useOlympiadStatisticDownload';
import OlympiadStatisticTable from './OlympiadStatisticTable';
import { ReactComponent as ErrorIcon } from '../../icons/error.svg';
import { ReactComponent as DownloadIcon } from 'assets/images/icons/download.svg';
import ModalV2 from 'features/shared/ModalV2';
import * as S from './OlympiadsStatisticStyled';

const DOWNLOAD_BTNS: Record<BtnState.Default | BtnState.Error, { label: string; icon: React.ReactNode }> = {
  [BtnState.Default]: { label: 'Скачать', icon: <DownloadIcon /> },
  [BtnState.Error]: { label: 'Повторить', icon: <ErrorIcon /> },
};

const DOWNLOAD_LEVELS: Array<{ id: LevelId; name: string }> = [
  { id: 'regions', name: 'Регионы' },
  { id: 'municipalities', name: 'Муниципалитеты' },
  { id: 'schools', name: 'Школы' },
];

type StatisticLocationState = {
  olympiad?: Olympiad;
  selectedYear?: OlympiadEducationYear;
};

const ReportError = ({ onClose }: { onClose: () => void }) => (
  <ModalV2 onClose={onClose} withoutPadding data-testid="olympiad-statistic-report-error">
    <S.ErrorCard>
      <S.ErrorTitle>Ошибка при подготовке отчета</S.ErrorTitle>
      <S.ErrorDescription>
        Попробуйте позже или напишите в поддержку: <br />
        <a href={`mailto:${supportEmail}`} target="_blank" rel="noopener noreferrer">
          {supportEmail}
        </a>
      </S.ErrorDescription>
      <PrimaryButton type="button" size="xlarge" onClick={onClose} data-testid="olympiad-statistic-report-error-close">
        Закрыть
      </PrimaryButton>
    </S.ErrorCard>
  </ModalV2>
);

const DownloadLevels = ({
  levelStates,
  onLevelClick,
  unavailable,
}: {
  levelStates: Record<LevelId, BtnState>;
  onLevelClick: (level: LevelId) => void;
  unavailable?: boolean;
}) => (
  <>
    <S.SectionTitle>Выберите охват статистики</S.SectionTitle>
    {unavailable ? (
      <S.StatisticUnavailableCard data-testid="olympiad-statistic-unavailable">
        <S.StatisticUnavailableIcon>
          <CalendarIcon />
        </S.StatisticUnavailableIcon>
        <S.StatisticUnavailableText>
          Статистика будет доступна на следующий день после начала олимпиады
        </S.StatisticUnavailableText>
      </S.StatisticUnavailableCard>
    ) : (
      <S.LevelsWrapper>
        {DOWNLOAD_LEVELS.map(({ id, name }) => {
          const state = levelStates[id];
          const config = DOWNLOAD_BTNS[state === BtnState.Error ? BtnState.Error : BtnState.Default];

          return (
            <S.LevelWrapper key={id} data-testid={`olympiad-statistic-level-${id}`}>
              <S.LevelName>{name}</S.LevelName>
              <LogClick eventType="click" eventInfo={`button_olympiad_statistic_${id}`} cd1={id}>
                <S.LevelActionButton
                  type="button"
                  size="medium"
                  loading={state === BtnState.Loading}
                  onClick={() => onLevelClick(id)}
                  data-testid={`olympiad-statistic-level-button-${id}`}
                >
                  {config.icon}
                  <span>{config.label}</span>
                </S.LevelActionButton>
              </LogClick>
            </S.LevelWrapper>
          );
        })}
      </S.LevelsWrapper>
    )}
  </>
);

const OlympiadsStatistic = () => {
  const { olympiadId } = useParams<{ olympiadId: string }>();
  const location = useLocation<StatisticLocationState>();
  const history = useHistory();
  const { authorized } = useAuthContext();
  const userData = useUserData();

  const selectedYear = location.state?.selectedYear ?? readSelectedOlympiadYear();
  const isTableYear = isTableStatisticYear(selectedYear);
  const olympiadFromState = location.state?.olympiad;

  const { olympiads, loading } = useOlympiadsFormControl(
    defaultOlympiadStatusesFilter,
    isTableYear ? selectedYear : [],
  );

  const olympiad = useMemo(
    () => olympiadFromState ?? olympiads.find((item) => item.olympiad_id === olympiadId),
    [olympiadFromState, olympiadId, olympiads],
  );

  const {
    data: tableData,
    loading: tableLoading,
    error: tableError,
  } = useOlympiadStatisticDetails(
    olympiadId,
    olympiad?.name,
    olympiad?.status as OlympiadStatus | undefined,
    selectedYear,
    isTableYear && !!olympiad?.status,
  );

  const { levelStates, showError, setShowError, onLevelClick } = useOlympiadStatisticDownload(olympiadId, selectedYear);

  const isFuture = olympiad?.status === OlympiadStatus.future;
  const isRegionUser = !!(userData?.isRegion || userData?.isLargeCity);

  if (loading && !olympiad) {
    return (
      <OlympiadsPage data-testid="olympiad-statistic-page">
        <Loader size={56} />
      </OlympiadsPage>
    );
  }

  return (
    <OlympiadsPage data-testid="olympiad-statistic-page">
      {olympiad && (
        <S.StatisticPageHeader>
          <LogClick eventType="click" eventInfo="button_olympiad_statistic_back">
            <S.BackButton type="button" onClick={() => history.push('/main')} aria-label="Вернуться" data-testid="olympiad-statistic-back-button">
              <BackBoldArrowIcon />
            </S.BackButton>
          </LogClick>
          {olympiad.icon && <S.OlympiadIcon src={olympiad.icon} alt="" />}
          <S.PageTitle>{olympiad.name}</S.PageTitle>
        </S.StatisticPageHeader>
      )}

      <OlympiadsContent>
        <S.StatisticMain>
          {olympiad && <OlympiadCard olympiad={olympiad} hideHeader showInformButton />}

          <S.ContentWrapper>
            {isTableYear ? (
              <OlympiadStatisticTable
                olympiadId={olympiadId}
                olympiadName={olympiad?.name}
                isRegionUser={isRegionUser}
                loading={tableLoading}
                error={tableError}
                data={tableData}
                status={(olympiad?.status as DetailsOlympiadStatus) ?? DetailsOlympiadStatus.passed}
                userType={userData?.userType ?? ''}
                largeCity={!!userData?.largeCity}
              />
            ) : (
              <DownloadLevels
                levelStates={levelStates}
                onLevelClick={onLevelClick}
                unavailable={isFuture}
              />
            )}
          </S.ContentWrapper>
        </S.StatisticMain>

        {authorized && (
          <OlympiadsSidebar>
            <OlympiadsManagerCard />
          </OlympiadsSidebar>
        )}
      </OlympiadsContent>

      {showError && <ReportError onClose={() => setShowError(false)} />}
    </OlympiadsPage>
  );
};

export default OlympiadsStatistic;